import React, { useEffect, useRef, useState } from 'react';
import { X, Building2, ArrowLeft } from 'lucide-react';
import { Customer } from '../../types/crm';
import { supabase, isSupabaseConfigured } from '../../lib/supabase';
import { formatTitleCase, normalizeCustomerNameFields, stripDiacritics } from '../../utils/textFormatting';
import { PhoneNumberInput } from '../shared/PhoneNumberInput';
import {
  buildCompanyContactFromForm,
  formatLocationOneLine,
  getPrimaryLocation,
} from '../../utils/customerRecordPersistence';

const FALLBACK_LANGUAGES = ['English', 'Irish', 'Polish', 'Romanian', 'Lithuanian', 'Portuguese', 'Spanish', 'Other'];

interface AddEditCustomerModalProps {
  customer?: Customer;
  onSave: (customer: Customer) => void | Promise<void>;
  onClose: () => void;
  /** Overrides the primary submit button label (e.g. create + add to session). */
  submitButtonLabel?: string;
  /** Overrides the dialog heading (defaults to Add/Edit CRM Record). */
  headingTitle?: string;
  /** Used for create flows that need a non-company default. */
  defaultCustomerType?: CustomerType;
  /** Optional company prefill for create flows. */
  initialCompanyName?: string;
  /** Pre-fetched language list from Admin → Languages. Falls back to DB query then hardcoded list. */
  availableLanguages?: string[];
}

type CustomerType = 'company' | 'individual';

export function AddEditCustomerModal({
  customer,
  onSave,
  onClose,
  submitButtonLabel,
  headingTitle,
  defaultCustomerType,
  initialCompanyName,
  availableLanguages: propLanguages
}: AddEditCustomerModalProps) {
  const submitLockRef = useRef(false);
  const draftCustomerIdRef = useRef(customer?.id || crypto.randomUUID());
  const draftLocationIdRef = useRef(
    getPrimaryLocation(customer)?.id || customer?.locations?.[0]?.id || crypto.randomUUID()
  );
  const primaryLocation = getPrimaryLocation(customer);

  const buildInitialFormData = () => ({
    customerType: (customer?.customerType || defaultCustomerType || 'individual') as CustomerType,
    companyRole: (customer?.companyRole || 'payer') as 'payer' | 'beneficiary',
    companyName: customer?.companyName || initialCompanyName || '',
    preferredLanguage: customer?.preferredLanguage || 'English',
    firstName: customer?.firstName || '',
    surname: customer?.surname || '',
    contactPhoneNumber:
      customer?.contactPhoneNumber || primaryLocation?.locationContactPhone || '',
    emailAddress: customer?.emailAddress || primaryLocation?.locationContactEmail || '',
    companyAddress: formatLocationOneLine(primaryLocation),
    companyContact: primaryLocation?.locationContactName || '',
    vatNumber: '',
    sageCode: customer?.sageCode || '',
  });

  const [languageOptions, setLanguageOptions] = useState<string[]>(propLanguages ?? FALLBACK_LANGUAGES);

  useEffect(() => {
    if (propLanguages?.length) { setLanguageOptions(propLanguages); return; }
    if (!isSupabaseConfigured()) return;
    supabase.from('course_languages').select('name').eq('is_active', true).order('name', { ascending: true })
      .then(({ data }) => {
        if (data?.length) {
          const names = data.map((r: { name?: string }) => String(r.name || '').trim()).filter(Boolean);
          if (names.length) setLanguageOptions([...names, 'Other']);
        }
      });
  }, [propLanguages]);

  const [formData, setFormData] = useState(buildInitialFormData);

  useEffect(() => {
    draftCustomerIdRef.current = customer?.id || draftCustomerIdRef.current;
    const nextPrimary = getPrimaryLocation(customer);
    draftLocationIdRef.current = nextPrimary?.id || customer?.locations?.[0]?.id || draftLocationIdRef.current;
    setFormData(buildInitialFormData());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer?.id]);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (formData.customerType === 'company' && !formData.companyName.trim()) {
      newErrors.companyName = 'Company name is required';
    }
    if (formData.customerType === 'company' && !formData.companyAddress.trim()) {
      newErrors.companyAddress = 'Company address is required';
    }
    if (formData.customerType === 'individual' && !formData.firstName.trim()) {
      newErrors.firstName = 'First name is required for individuals';
    }
    if (formData.customerType === 'individual' && !formData.surname.trim()) {
      newErrors.surname = 'Surname is required for individuals';
    }
    if (
      formData.customerType === 'individual' &&
      !formData.contactPhoneNumber.trim() &&
      !formData.emailAddress.trim()
    ) {
      newErrors.contactPhoneNumber = 'Enter an email or mobile number';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitLockRef.current || isSaving) return;
    if (!validateForm()) return;
    submitLockRef.current = true;

    let fallbackCompanyName: string | null = formData.companyName.trim() || null;
    if (formData.customerType === 'individual') {
      // Preserve a real employer name if it exists; never store the person's own name as companyName
      const existingOrg = (customer?.companyName || '').trim();
      const oldFullName = `${customer?.firstName || ''} ${customer?.surname || ''}`.trim().toLowerCase();
      const hasRealEmployer = existingOrg && existingOrg.toLowerCase() !== oldFullName;
      fallbackCompanyName = hasRealEmployer ? existingOrg : null;
    }

    const formattedFirstName = formData.firstName.trim() ? formatTitleCase(formData.firstName) : undefined;
    const formattedSurname = formData.surname.trim() ? formatTitleCase(formData.surname) : undefined;
    const formattedCompanyName = fallbackCompanyName ? formatTitleCase(fallbackCompanyName) : null;
    const formattedCompanyContact = formData.companyContact.trim()
      ? formatTitleCase(formData.companyContact)
      : undefined;
    const formattedCompanyPhone = formData.contactPhoneNumber.trim() || undefined;
    const formattedCompanyEmail = formData.emailAddress.trim() || undefined;

    const companyContacts =
      formData.customerType === 'company'
        ? buildCompanyContactFromForm({
            contactName: formattedCompanyContact,
            email: formattedCompanyEmail,
            phone: formattedCompanyPhone,
            existingContacts: customer?.contacts || [],
          })
        : customer?.contacts || [];

    const existingPrimary = getPrimaryLocation(customer);
    const addressLine1 = formData.companyAddress.trim();

    const customerData: Customer = normalizeCustomerNameFields({
      id: customer?.id || draftCustomerIdRef.current,
      customerType: formData.customerType,
      companyRole: formData.customerType === 'company' ? formData.companyRole : null,
      companyName: formattedCompanyName ?? '',
      preferredLanguage: formData.customerType === 'individual' ? formData.preferredLanguage.trim() || undefined : customer?.preferredLanguage,
      firstName: formattedFirstName,
      surname: formattedSurname,
      contactPhoneNumber: formattedCompanyPhone,
      emailAddress: formattedCompanyEmail,
      sageCode: formData.sageCode.trim() || undefined,
      locations: formData.customerType === 'company'
        ? [
            {
              id: existingPrimary?.id || draftLocationIdRef.current,
              addressLine1,
              addressLine2: existingPrimary?.addressLine2 || '',
              addressLine3: existingPrimary?.addressLine3 || '',
              city: existingPrimary?.city || addressLine1,
              countyState: existingPrimary?.countyState || '',
              eircodePostcode: existingPrimary?.eircodePostcode || '',
              country: existingPrimary?.country || 'Ireland',
              locationContactName: formattedCompanyContact,
              locationContactEmail: formattedCompanyEmail || null,
              locationContactPhone: formattedCompanyPhone || '',
              locationNotes: existingPrimary?.locationNotes || '',
            }
          ]
        : addressLine1
          ? [
              {
                id: existingPrimary?.id || draftLocationIdRef.current,
                addressLine1,
                addressLine2: existingPrimary?.addressLine2 || '',
                addressLine3: existingPrimary?.addressLine3 || '',
                city: existingPrimary?.city || addressLine1,
                countyState: existingPrimary?.countyState || '',
                eircodePostcode: existingPrimary?.eircodePostcode || '',
                country: existingPrimary?.country || 'Ireland',
                locationContactName: existingPrimary?.locationContactName || undefined,
                locationContactEmail: existingPrimary?.locationContactEmail || null,
                locationContactPhone: existingPrimary?.locationContactPhone || '',
                locationNotes: existingPrimary?.locationNotes || '',
              }
            ]
          : customer?.locations || [],
      otherInfoNotes:
        formData.customerType === 'company'
          ? [`VAT: ${formData.vatNumber.trim() || 'N/A'}`].join('\n')
          : customer?.otherInfoNotes,
      contacts: companyContacts,
      numCertsGenerated: customer?.numCertsGenerated || 0,
      isPaid: customer?.isPaid || false,
      lastPaymentDate: customer?.lastPaymentDate || '',
      paymentHistory: customer?.paymentHistory || [],
      currentBatchCertificates: customer?.currentBatchCertificates || [],
      generatedCertificates: customer?.generatedCertificates || []
    });

    setIsSaving(true);
    try {
      await onSave(customerData);
      onClose();
    } finally {
      setIsSaving(false);
      submitLockRef.current = false;
    }
  };

  const handleChange = (field: keyof typeof formData, value: string | boolean) => {
    setFormData(prev => {
      const next = { ...prev, [field]: value };
      if (field === 'customerType') {
        const selectedType = value as CustomerType;
        if (selectedType === 'individual') {
          next.companyAddress = '';
          next.companyContact = '';
          next.vatNumber = '';
        } else {
          next.firstName = '';
          next.surname = '';
          next.emailAddress = '';
        }
      }
      return next;
    });
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
    if (field === 'emailAddress' || field === 'contactPhoneNumber') {
      setErrors((prev) => {
        if (!prev.contactPhoneNumber && !prev.emailAddress) return prev;
        const next = { ...prev };
        delete next.contactPhoneNumber;
        delete next.emailAddress;
        return next;
      });
    }
  };

  const handleNameChange = (field: keyof typeof formData, value: string) => {
    handleChange(field, stripDiacritics(value));
  };

  const textInput = (
    field: keyof typeof formData,
    label: string,
    placeholder = '',
    disabled = false,
    stripAccents = false
  ) => (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-1">{label}</label>
      <input
        type="text"
        value={String(formData[field] ?? '')}
        onChange={(e) => (stripAccents ? handleNameChange(field, e.target.value) : handleChange(field, e.target.value))}
        placeholder={placeholder}
        disabled={disabled}
        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400 ${
          errors[field] ? 'border-red-300 bg-red-50' : 'border-slate-200'
        }`}
      />
      {errors[field] && <p className="mt-1 text-xs text-red-600">{errors[field]}</p>}
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4">
      <div className="bg-white rounded-2xl shadow-2xl border border-white/20 w-full max-w-4xl max-h-[90dvh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <button onClick={onClose} className="inline-flex items-center px-3 py-2 text-slate-600 hover:bg-slate-100 rounded-lg">
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </button>
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-bold text-slate-800">
              {headingTitle ?? (customer ? 'Edit CRM Record' : 'Add CRM Record')}
            </h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-xl">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Record Type</label>
              <select
                value={formData.customerType}
                onChange={(e) => handleChange('customerType', e.target.value as CustomerType)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg"
              >
                <option value="company">Company</option>
                <option value="individual">Individual (standalone)</option>
              </select>
            </div>
            {formData.customerType === 'individual'
              ? textInput('firstName', 'Name *', '', false, true)
              : textInput('companyName', 'Company Name *', '', false, true)}
            {formData.customerType === 'individual'
              ? textInput('surname', 'Last Name *', '', false, true)
              : textInput('companyAddress', 'Company Address *')}
            {formData.customerType === 'company' && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Company Category</label>
                <select
                  value={formData.companyRole}
                  onChange={(e) => handleChange('companyRole', e.target.value as 'payer' | 'beneficiary')}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                >
                  <option value="payer">Category 1 - Payer</option>
                  <option value="beneficiary">Category 2 - Beneficiary</option>
                </select>
              </div>
            )}
          </div>

          {formData.customerType === 'individual' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Preferred Language</label>
                <select
                  value={formData.preferredLanguage}
                  onChange={(e) => handleChange('preferredLanguage', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                >
                  {languageOptions.map((lang) => (
                    <option key={lang} value={lang}>{lang}</option>
                  ))}
                </select>
              </div>
              {textInput('emailAddress', 'Email')}
              <PhoneNumberInput
                label="Mobile number"
                value={formData.contactPhoneNumber}
                onChange={(value) => handleChange('contactPhoneNumber', value)}
                error={errors.contactPhoneNumber}
              />
              <p className="col-span-full -mt-2 text-xs text-slate-500">At least one of email or mobile is required.</p>
              {textInput('companyAddress', 'Address (optional)')}
              {textInput('sageCode', 'Code (Sage)')}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {textInput('sageCode', 'Code (Sage)')}
              {textInput('companyContact', 'Company Contact (optional)', '', false, true)}
              {textInput('emailAddress', 'Email (optional)')}
              <PhoneNumberInput
                label="Phone number (optional)"
                value={formData.contactPhoneNumber}
                onChange={(value) => handleChange('contactPhoneNumber', value)}
              />
              {textInput('vatNumber', 'VAT Number (optional)')}
            </div>
          )}

          <div className="flex space-x-3 pt-2">
            <button type="button" onClick={onClose} disabled={isSaving} className="flex-1 px-4 py-3 border border-slate-300 text-slate-700 font-medium rounded-xl hover:bg-slate-50 disabled:opacity-50">
              Cancel
            </button>
            <button type="submit" disabled={isSaving} className="flex-1 px-4 py-3 bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-medium rounded-xl disabled:opacity-50">
              {isSaving ? 'Saving…' : submitButtonLabel ?? (customer ? 'Update Record' : 'Create Record')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
